import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { supabase } from "@/lib/supabase";

interface Article {
  id: string;
  title: string;
  author: string;
  image_url: string;
  date: string;
  category: string;
  read_time: string;
  excerpt: string;
}

export default function ArticlesSection() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  useScrollAnimation();

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("articles")
      .select("id, title, author, image_url, date, category, read_time, excerpt")
      .eq("status", "Published")
      .order("date", { ascending: false })
      .limit(3);
    if (error) {
      console.error("Error fetching articles:", error);
    } else {
      setArticles(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Re-observe scroll-animated elements after articles load
  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    const elements = document.querySelectorAll(".animate-on-scroll:not(.visible)");
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [loading]);

  const formatDate = (d: string) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("");

  return (
    <section id="news" className="bg-white py-20 md:py-28 px-4 md:px-8 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14 animate-on-scroll">
          <span className="px-4 py-1.5 rounded-full bg-secondary text-primary text-xs font-inter font-semibold mb-5 tracking-wide uppercase inline-block">
            Latest News
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-saria font-bold text-dark leading-tight max-w-2xl mx-auto">
            We have a commitment to serve our community
          </h2>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-primary text-2xl"></i>
          </div>
        )}

        {!loading && articles.length === 0 && (
          <div className="text-center py-12">
            <p className="text-stone-500">No articles available at the moment.</p>
          </div>
        )}

        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {articles.map((article, idx) => (
              <Link
                key={article.id}
                to={`/news/${article.id}`}
                className={`animate-on-scroll animate-on-scroll-delay-${idx} group bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-500 border border-stone-100 block`}
              >
                {/* Image */}
                <div className="relative h-[220px] overflow-hidden">
                  <img
                    src={article.image_url || "https://readdy.ai/api/search-image?query=Community%20development%20news%20and%20journalism%20in%20Cameroon%20vibrant%20storytelling%20atmosphere%20warm%20lighting%20simple%20clean%20background%20photorealistic%20style&width=800&height=500&seq=article-fallback&orientation=landscape"}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 backdrop-blur-sm text-primary text-xs px-3 py-1.5 rounded-full font-inter font-semibold shadow-sm">
                      {article.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center border border-primary/20">
                      <span className="text-xs text-primary font-bold">{getInitials(article.author)}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-dark font-medium">{article.author}</span>
                      <span className="text-xs text-stone-400">{formatDate(article.date)} &middot; {article.read_time || "5 min read"}</span>
                    </div>
                  </div>

                  <h3 className="text-lg font-saria font-bold text-dark leading-snug mb-4 group-hover:text-primary transition-colors duration-300">
                    {article.title}
                  </h3>

                  <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                    <span className="text-xs text-stone-400">{formatDate(article.date)}</span>
                    <span className="flex items-center gap-1.5 text-primary font-semibold text-sm group-hover:gap-2.5 transition-all duration-300">
                      Read article
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}